import {
  BoxOfferRulePeriod,
  BoxOfferValidity,
  OfferAutonomousPurchase,
  RecurringPeriod,
} from "@/modules/boxOffer/entities/boxOfferTypes"
import type { MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import type { Language } from "@/modules/language/utils/languageUtils"
import { CoachOfferType, CoachPaymentOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { word } from "@modules/core/utils/i18n"
import { z } from "zod"

export type CoachOfferRule = {
  value: number
  period: BoxOfferRulePeriod
}

export const coachOfferRuleValidationSchema = (rules: CoachOfferRule[]) =>
  z
    .object({
      value: z.number().min(1, word("global.form.minimumValue", { minValue: 1 })),
      period: z.enum(BoxOfferRulePeriod),
    })
    .refine(
      ({ value, period }) => {
        const existingRule = rules.find((r) => r.period === period)
        if (existingRule?.value === value) return true

        const dayRule = rules.find((r) => r.period === BoxOfferRulePeriod.DAY)
        const weekRule = rules.find((r) => r.period === BoxOfferRulePeriod.WEEK)
        const monthRule = rules.find((r) => r.period === BoxOfferRulePeriod.MONTH)
        switch (period) {
          case BoxOfferRulePeriod.DAY:
            return weekRule?.value
              ? value !== existingRule?.value && value <= weekRule.value
              : value !== existingRule?.value && (!monthRule || value <= monthRule.value / 30)
          case BoxOfferRulePeriod.WEEK:
            return (
              value !== existingRule?.value &&
              (!dayRule || value <= dayRule.value * 7) &&
              (!monthRule || value <= monthRule.value / 4)
            )
          case BoxOfferRulePeriod.MONTH:
            return (
              value !== existingRule?.value &&
              (!dayRule || value <= dayRule.value * 30) &&
              (!weekRule || value <= weekRule.value * 4)
            )
          default:
            return false
        }
      },
      { error: word("box.offer.form.infos.restrictedPeriod.error"), path: ["value"] },
    )

export type CoachOfferFormValues = {
  type: CoachOfferType | null
  image: MediaItemFormType | null
  name: string
  description: string
  rules: CoachOfferRule[]
  categoryIds: string[]
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase: OfferAutonomousPurchase
  defaultHideSessionContent: boolean
  paymentOffers: CoachOfferPricingFormValues[]
}

export const coachOfferFormDefaultValues: CoachOfferFormValues = {
  type: null,
  image: null,
  name: "",
  description: "",
  rules: [],
  categoryIds: [],
  showcaseDisplay: true,
  priceDisplay: true,
  autonomousPurchase: OfferAutonomousPurchase.PUBLIC,
  defaultHideSessionContent: false,
  paymentOffers: [],
}

export type CoachOfferPricingFormValues = {
  id?: string
  price: number | null
  recurringPeriod: RecurringPeriod | null
  type: CoachPaymentOfferType
  title: string
  count: number | null
  minimumCommitmentPeriod: string | null
  commitmentPeriodAutoRenewal: boolean | null
  validityPeriodMonths: number | null
  validitySwitch: BoxOfferValidity
  displayOrder: number | null
  renewable: boolean | null
  isTrialSession: boolean
}

export const coachOfferFreePricingFormDefaultValues: CoachOfferPricingFormValues = {
  type: CoachPaymentOfferType.Free,
  price: 0,
  title: "",
  count: null,
  minimumCommitmentPeriod: null,
  commitmentPeriodAutoRenewal: null,
  validitySwitch: BoxOfferValidity.UNLIMITED,
  validityPeriodMonths: null,
  displayOrder: null,
  renewable: null,
  recurringPeriod: null,
  isTrialSession: false,
}
export const coachOfferSubscriptionPricingFormDefaultValues: CoachOfferPricingFormValues = {
  type: CoachPaymentOfferType.Subscription,
  price: 0,
  title: "",
  count: null,
  minimumCommitmentPeriod: null,
  commitmentPeriodAutoRenewal: null,
  validitySwitch: BoxOfferValidity.UNLIMITED,
  validityPeriodMonths: null,
  displayOrder: null,
  renewable: null,
  recurringPeriod: RecurringPeriod.MONTHLY,
  isTrialSession: false,
}
export const coachOfferTicketBookPricingFormDefaultValues: CoachOfferPricingFormValues = {
  type: CoachPaymentOfferType.TicketsBook,
  price: 0,
  title: "",
  count: null,
  minimumCommitmentPeriod: null,
  commitmentPeriodAutoRenewal: null,
  validitySwitch: BoxOfferValidity.UNLIMITED,
  validityPeriodMonths: null,
  displayOrder: null,
  renewable: null,
  recurringPeriod: null,
  isTrialSession: false,
}

export const CoachOfferPricingValidationSchema = (_language: Language) =>
  z.object({
    type: z.enum(CoachPaymentOfferType),
    price: z
      .number()
      .min(0, word("global.form.minimumValue", { minValue: 0 }))
      .nullable(),
    title: z.string().min(1, word("global.form.required")),
    count: z
      .number()
      .min(1, word("global.form.minimumValue", { minValue: 0 }))
      .nullable(),
    minimumCommitmentPeriod: z.string().nullable(),
    commitmentPeriodAutoRenewal: z.boolean().nullable(),
    validityPeriodMonths: z
      .number()
      .min(1, word("global.form.minimumValue", { minValue: 1 }))
      .nullable(),
    validitySwitch: z.enum(BoxOfferValidity),
    displayOrder: z.number().min(0).nullable(),
    renewable: z.boolean().nullable(),
    recurringPeriod: z.enum(RecurringPeriod).nullable(),
    isTrialSession: z.boolean(),
  })

export const CoachOfferValidationSchema = (language: Language) =>
  z.object({
    type: z.enum(CoachOfferType),
    name: z.string().min(1, word("global.form.required")),
    description: z.string().min(1, word("global.form.required")),
    image: z
      .custom<MediaItemFormType | null>()
      .refine((value) => !!value?.key)
      .nullable(),
    rules: z.array(z.object({ value: z.number(), period: z.enum(BoxOfferRulePeriod) })),
    categoryIds: z.array(z.string()),
    showcaseDisplay: z.boolean(),
    priceDisplay: z.boolean(),
    autonomousPurchase: z.enum(OfferAutonomousPurchase),
    defaultHideSessionContent: z.boolean(),
    paymentOffers: z.array(CoachOfferPricingValidationSchema(language)),
  })
